import React, { useState, useEffect, useCallback } from 'react';
import { inpatientAPI } from '../../services/api';
import SelectPaymentModal from './SelectPaymentModal'; // Commented out for now - will be used later
import { useTranslation } from "react-i18next";

const CloseInpatientModal = ({
  isOpen,
  onClose,
  inpatientId,
  onSuccess,
  paymentAmount,
  totalAmount,
  downpayment
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-SA" : "en-US";
  const isRTL = i18n.dir() === "rtl";
  const formatAmount = useCallback(
    (value) => {
      const number = Number(value);
      if (Number.isNaN(number)) return "0.00";
      return number.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    [locale]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Commented out SelectPaymentModal state - will be used later
  // const [showPaymentModal, setShowPaymentModal] = useState(false);
  // const [selectedPayment, setSelectedPayment] = useState(null);

  // Mock remaining amount
  const mockRemainingAmount = paymentAmount;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError('');
      // Commented out SelectPaymentModal reset - will be used later
      // setShowPaymentModal(false);
      // setSelectedPayment(null);
    }
  }, [isOpen]);

  // Commented out payment modal handlers - will be used later
  // const handleProceedToPayment = () => {
  //   setShowPaymentModal(true);
  // };

  // const handlePaymentSave = async (paymentData) => {
  //   setSelectedPayment(paymentData);
  //   setShowPaymentModal(false);

  //   // Directly proceed to close the inpatient visit
  //   setLoading(true);
  //   setError('');

  //   try {
  //     // Use selected payment data for the API call
  //     const closeData = {
  //       payment_type: paymentData.method,
  //       payment_note: paymentData.notes,
  //       status: 'completed',
  //     };

  //     await inpatientAPI.closeInpatientVisit(inpatientId, closeData);

  //     if (onSuccess) {
  //       onSuccess();
  //     }
  //     onClose();
  //   } catch (err) {
  //     console.error('Error closing inpatient visit:', err);
  //     setError(err.response?.data?.message || 'Failed to close inpatient visit. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handlePaymentCancel = () => {
  //   setShowPaymentModal(false);
  // };

  // New direct close visit handler
  const handleCloseVisit = async () => {
    setLoading(true);
    setError('');

    try {
      // Close the inpatient visit directly without payment modal
      // Include required payment fields with default values to avoid 422 error
      const closeData = {
        payment_type: 1, // Default payment type (usually cash or first available)
        payment_note: t("receptionInpatients.close.defaultNote"),
        status: 'completed',
      };

      await inpatientAPI.closeInpatientVisit(inpatientId, closeData);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error closing inpatient visit:', err);
      setError(err.response?.data?.message || t("receptionInpatients.close.errors.generic"));
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div
      className="reception-modal-overlay"
      dir={i18n.dir()}
      lang={i18n.language}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="reception-modal">
        <div className="reception-modal-header" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 className="reception-modal-title" style={{ textAlign: isRTL ? 'right' : 'left' }}>
            {t("receptionInpatients.close.header.title")}
          </h2>
        </div>
        <div className="reception-modal-content">
          {/* Main Content */}
          <div className="confirmation-message" style={{ textAlign: 'center', marginBottom: '24px' }}>

            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
              {t("receptionInpatients.close.header.subtitle")}
            </h3>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: '1.5', marginBottom: '16px' }}>
              {t("receptionInpatients.close.header.description")}
            </p>
          </div>

          {/* Payment Breakdown Display */}
          <div style={{
            background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
            border: '2px solid #0EA5E9',
            borderRadius: '16px',
            padding: '32px 24px',
            margin: '24px 0',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 8px 25px rgba(14, 165, 233, 0.15)'
          }}>
            {/* Payment Icon */}
            <div style={{
              position: 'absolute',
              top: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '32px',
              height: '32px',
              background: '#3B82F6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
            </div>

            <div style={{
              fontSize: '16px',
              color: '#0369A1',
              fontWeight: '700',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t("receptionInpatients.close.summary.title")}
            </div>

            {/* Payment Breakdown */}
            {/* First Row: Total Amount */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                flex: '1',
                background: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '12px',
                padding: '16px 12px',
                border: '1px solid rgba(14, 165, 233, 0.2)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#0369A1',
                  fontWeight: '600',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t("receptionInpatients.close.summary.total")}
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  color: '#0C4A6E'
                }}>
                  {formatAmount(totalAmount || 0)}
                </div>
              </div>
            </div>

            {/* Second Row: Downpayment */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                flex: '1',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '12px',
                padding: '16px 12px',
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#059669',
                  fontWeight: '600',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t("receptionInpatients.close.summary.downpayment")}
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  color: '#047857'
                }}>
                  {formatAmount(downpayment || 0)}
                </div>
              </div>
            </div>

            {/* Separator Line */}
            <div style={{
              borderTop: '3px solid #D1D5DB',
              marginBottom: '20px'
            }} />

            {/* Third Row: Remaining Balance - Emphasized */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                flex: '1',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '12px',
                padding: '20px 24px',
                border: '2px solid rgba(239, 68, 68, 0.3)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#DC2626',
                  fontWeight: '600',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t("receptionInpatients.close.summary.due")}
                </div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '900',
                  color: '#B91C1C'
                }}>
                  {formatAmount(paymentAmount || 0)}
                </div>
              </div>
            </div>

      
          </div>

          {error && (
          <div className="error-message" style={{ textAlign: isRTL ? 'right' : 'left' }}>
              {error}
            </div>
          )}

          <div className="reception-modal-actions">
            <button
              onClick={() => onClose && onClose()}
              className="payment-modal-cancel-btn"
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "2px solid #e2e8f0",
                background: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
                cursor: "pointer",
                opacity: 1,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748b",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="reception-action-btn"
              onClick={handleCloseVisit}
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #10B981, #059669)",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "linear-gradient(135deg, #059669, #047857)";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 16px rgba(16, 185, 129, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "linear-gradient(135deg, #10B981, #059669)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.3)";
              }}
            >
              {loading ? t("receptionInpatients.close.buttons.processing") : t("receptionInpatients.close.buttons.submit")}
            </button>
            {/* Commented out payment modal button - will be used later */}
            {/* 
            <button
              type="button"
              className="reception-action-btn"
              onClick={handleProceedToPayment}
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "linear-gradient(135deg, #2563EB, #1D4ED8)";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "linear-gradient(135deg, #3B82F6, #2563EB)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
              }}
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </button>
            */}
          </div>
        </div>
      </div>

      {/* Commented out SelectPaymentModal - will be used later */}
      {/* 
      <SelectPaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentCancel}
        onSave={handlePaymentSave}
        price={mockRemainingAmount}
        title="Select Payment Method"
        isLoading={loading}
      />
      */}


      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
   
        
        .confirmation-message {
          text-align: center;
        }
        
        .confirmation-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .error-message {
          background: #FEF2F2;
          border: 1px solid #FECACA;
          color: #DC2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin: 16px 0;
          font-size: 14px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default CloseInpatientModal;
